/**
 * 챗봇 목 (WBS 3) — **서버 역할을 대신한다.**
 *
 * 백엔드와 병렬로 가기 위한 임시 다리다. 계약이 설계 §7 + `docs/2026-08-20-chat-front-contract.md`
 * 에 고정돼 있어서 화면을 먼저 끝까지 만들 수 있다. 백엔드가 뜨면 `chat.ts` 의 `USE_MOCK` 만
 * `false` 로 바꾸고, 안정되면 이 파일과 `chat.mock.data.ts` 를 지운다.
 *
 * **여기가 하는 일이 곧 서버가 할 일이다.** 그래서 아래를 지킨다.
 *   · 문안을 스토어에서 **꺼내 완성된 문장으로** 실어 보낸다 (버튼 라벨까지)
 *   · 차단 판정을 **규칙 스토어로** 한다 — 어드민에서 규칙을 고치면 대화가 그 자리에서 바뀐다
 *   · 「문안 없는 자리」 대조도 여기서 한다 — 프론트가 카탈로그와 맞추지 않는다
 *
 * goal §6 이 요구한 상태 넷을 전부 낸다: 정상 추천 3건(CTA 3분기) · 되묻기 · 매칭 실패 ·
 * 금지발화 차단.
 */

import { ChatApiError } from './chat.error';
import type {
  ChatCopy,
  ChatCopyList,
  ChatFollowUp,
  ChatGuardRule,
  ChatProduct,
  ChatTurn,
  CreateChatCopyRequest,
  CreateFollowUpRequest,
  DryRunCase,
  DryRunOutcome,
  DryRunRequest,
  DryRunResult,
  DryRunRule,
  DryRunRuleHit,
  DryRunVerdict,
  FallbackNode,
  MissingChatCopy,
  UpdateFollowUpRequest,
  UpdateGuardRuleRequest,
} from './chat';
import { CHAT_COPY_SEED, CHAT_FOLLOW_UP_SEED, CHAT_GUARD_RULE_SEED, CHAT_PRODUCT_SEED, DRY_RUN_CASE_SEED, FALLBACK_PRODUCT_CODES, FALLBACK_TREE_OPTIONS } from './chat.mock.data';

/** 로딩 상태가 눈에 보일 만큼만. 화면이 빈 화면으로 깜빡이는지 여기서 드러난다. */
const LATENCY_MS = 200;

const wait = <T>(value: T): Promise<T> => new Promise(resolve => setTimeout(() => resolve(value), LATENCY_MS));

/** 서버가 `updated_at` 에 적는 모양(`2026-08-20T14:22:10`)과 같게 자른다. */
const nowStamp = (): string => new Date().toISOString().slice(0, 19);

// ── 스토어 ───────────────────────────────────────────
//
// 모듈 수명 동안 유지된다. 어드민에서 문안을 고치고 `/chat` 을 열면 바뀐 문장이 나온다 —
// 「고치면 열려 있는 챗봇 문장이 바로 바뀐다」(설계 §1-4)를 목에서도 볼 수 있게 한 것이다.
// 새로고침하면 시드로 돌아간다. 서버가 붙으면 DB 가 그 자리를 대신한다.

let copies: ChatCopy[] = CHAT_COPY_SEED;
let followUps: ChatFollowUp[] = CHAT_FOLLOW_UP_SEED;
let guardRules: ChatGuardRule[] = CHAT_GUARD_RULE_SEED;

/**
 * 세션당 되묻기 상한. 서버의 `chat.follow-up-max` 기본값을 그대로 흉내 낸다 (2026-08-28,
 * 1회에서 늘었다). 서버는 `chat_session.follow_up_count` 에 조건부 UPDATE
 * (`count = count + 1 WHERE count < :max`)로 판정과 쓰기를 한 문장에 담는다.
 */
const FOLLOW_UP_MAX = 5;

const sessions = new Map<string, { followUpCount: number }>();

let sessionCounter = 0;

// ── 서버가 하는 일 ───────────────────────────────────

/**
 * 문안을 꺼낸다. 못 찾으면 **사고 방지용 문구**만 낸다.
 *
 * 진짜 문안을 여기 두면 어드민 편집이 성립하지 않는다(설계 §5.1).
 */
function copyText(copyKey: string): string {
  return copies.find(row => row.copyKey === copyKey)?.textKo ?? '안내 문구를 불러오지 못했습니다.';
}

/** `cta` 로 버튼 문안을 고르는 것은 **서버 일이다.** 화면에는 이 표가 없다. */
const CTA_COPY_KEY = {
  ORDER: 'CTA_ORDER',
  INQUIRY: 'CTA_INQUIRY',
  INTAKE: 'CTA_INTAKE',
} as const;

/** 카탈로그 상품에 버튼 라벨을 붙여 카드로 만든다. 금액은 손대지 않는다. */
function buildProducts(codes: string[]): ChatProduct[] {
  return codes
    .map(code => CHAT_PRODUCT_SEED[code])
    .filter((product): product is (typeof CHAT_PRODUCT_SEED)[string] => Boolean(product))
    .map(product => ({ ...product, ctaLabel: copyText(CTA_COPY_KEY[product.cta]) }));
}

/** 추천 문안은 카테고리별로 갈리고, 없으면 기본으로 떨어진다(설계 §5.1). */
function recommendText(categoryCode: string): string {
  const scoped = copies.find(row => row.copyKey === `TEMPLATE_RECOMMEND_${categoryCode}`);
  return scoped ? scoped.textKo : copyText('TEMPLATE_RECOMMEND_DEFAULT');
}

/** 규칙 하나가 문장에 걸리나. 정규식이 깨져 있으면 안 걸린 것으로 본다. */
function matches(pattern: string, text: string): boolean {
  try {
    return new RegExp(pattern).test(text);
  } catch {
    return false;
  }
}

/** 응답 골격. 필드가 항상 다 있다 — 서버가 `null` 로 내려주기로 했다(백엔드 회신 ③). */
function turn(sessionUuid: string, partial: Partial<ChatTurn> & Pick<ChatTurn, 'mode' | 'message'>): ChatTurn {
  return {
    sessionUuid,
    disclaimer: copyText('DISCLAIMER'),
    products: [],
    node: null,
    fallbackReason: null,
    followUpAvailable: (sessions.get(sessionUuid)?.followUpCount ?? 0) < FOLLOW_UP_MAX,
    ...partial,
  };
}

/** 폴백 트리 루트. LLM 이 죽었을 때만 쓰는 경로라 선택지가 맞다(설계 §2). */
function fallbackNode(): FallbackNode {
  return {
    nodeId: 'ROOT',
    options: FALLBACK_TREE_OPTIONS.map(({ optionId, label }) => ({ optionId, label })),
  };
}

// ── 고객 발화 라우팅 ─────────────────────────────────
//
// 목이 LLM 을 흉내 내는 자리다. **키워드 표는 서버가 붙으면 통째로 사라진다** — 실제로는
// Gemini 가 분류하고 서버가 코드를 대조한다. 여기서는 goal §6 의 상태 넷을 전부 밟을 수
// 있게만 갈라 둔다.
//
//   증거인멸·형량 같은 말 → REFUSAL   (규칙 스토어가 판정한다)
//   짧고 모호한 발화        → FOLLOW_UP (세션당 최대 5회 — chat.follow-up-max 기본값)
//   5회를 다 쓴 뒤의 모호함 → FALLBACK (LOW_CONFIDENCE · 근접 상품 · node 없음)
//   계약·노무 키워드        → NORMAL
//   그 밖                   → FALLBACK (UNKNOWN_SERVICE · 기본 3종 · 트리)

const INTENT_ROUTES: Array<{ categoryCode: string; keywords: RegExp; productCodes: string[] }> = [
  { categoryCode: 'CONT', keywords: /계약서|계약|검토|작성|공급|용역|비밀유지|납품/, productCodes: ['CONT-01', 'CONT-04', 'CONT-08'] },
  { categoryCode: 'LAB', keywords: /해고|임금|퇴직금|근로|직원|노무|인사/, productCodes: ['LAB-01', 'CONS-03', 'CONT-08'] },
  { categoryCode: 'LIT', keywords: /내용증명|미지급|대금|분쟁|소송|독촉|손해배상/, productCodes: ['LIT-02', 'CONT-08', 'CONS-03'] },
];

/** 공백을 뺀 길이가 이보다 짧으면 「무슨 계약인지」를 판단할 근거가 없다고 본다. */
const AMBIGUOUS_LENGTH = 12;

function isAmbiguous(text: string): boolean {
  return text.replace(/\s/g, '').length < AMBIGUOUS_LENGTH;
}

/** 카테고리 전용 질문을 먼저, 없으면 공통 질문을 쓴다. 꺼진 질문은 고르지 않는다. */
function pickFollowUp(categoryCode: string | null): ChatFollowUp | undefined {
  const enabled = followUps.filter(row => row.enabled);
  return enabled.find(row => row.categoryCode === categoryCode) ?? enabled.find(row => row.categoryCode === null);
}

// ── 고객 API ─────────────────────────────────────────

export function startChatSession(): Promise<ChatTurn> {
  sessionCounter += 1;
  const sessionUuid = `mock-session-${sessionCounter}`;
  sessions.set(sessionUuid, { followUpCount: 0 });

  return wait(turn(sessionUuid, { mode: 'NORMAL', message: copyText('GREETING') }));
}

export function sendChatMessage(sessionUuid: string, text: string): Promise<ChatTurn> {
  const session = sessions.get(sessionUuid) ?? { followUpCount: 0 };
  sessions.set(sessionUuid, session);

  // ① 입력 가드레일 — 걸리면 LLM 을 부르지 않는다. 카드도 없다.
  //
  // **어느 규칙에 걸렸는지를 응답에 싣지 않는다.** 사유를 알려주면 표현만 바꿔 다시 시도하는
  // 길을 열어 준다. 사유는 서버 기록과 어드민 dry-run 에만 남는다 — `chat.ts` 의 `ChatTurn`
  // 아래 주석에 경위가 있다.
  const blocked = guardRules.find(rule => rule.enabled && rule.stage === 'INPUT' && matches(rule.pattern, text));
  if (blocked) {
    return wait(turn(sessionUuid, { mode: 'REFUSAL', message: copyText(blocked.reasonKey) }));
  }

  const route = INTENT_ROUTES.find(candidate => candidate.keywords.test(text));

  // ② 되묻기 — 정보가 부족할 때만, 세션당 최대 5회. 카드를 같이 내지 않는다.
  // 상한 판정을 먼저 하고 세는 순서까지 서버와 같다(조건부 UPDATE 흉내).
  if (session.followUpCount < FOLLOW_UP_MAX && isAmbiguous(text)) {
    const followUp = pickFollowUp(route?.categoryCode ?? null);
    sessions.set(sessionUuid, { followUpCount: session.followUpCount + 1 });
    return wait(turn(sessionUuid, { mode: 'FOLLOW_UP', message: copyText(followUp?.copyKey ?? 'FOLLOW_UP_GENERIC') }));
  }

  // ②-2 되묻기 소진 — 그래도 모호하면 「가장 근접한 것」을 낸다 (2026-08-28).
  //
  // **트리를 내리지 않는다.** 다섯 번을 묻고 답까지 받은 고객을 처음부터 다시 고르는
  // 선택지로 보내면 그 다섯 번이 버려진다(제품 결정). 서버는 모델이 대화를 보고 고른
  // 코드를 얹는데, 목은 대화 기억이 없으니 키워드 라우트가 있으면 그쪽을, 없으면 기본
  // 3종을 얹는다 — 화면이 봐야 하는 모양(카드 있음 + node 없음 + LOW_CONFIDENCE)은 같다.
  if (isAmbiguous(text)) {
    return wait(
      turn(sessionUuid, {
        mode: 'FALLBACK',
        message: copyText('FALLBACK_NEAREST_MATCH'),
        products: buildProducts((route?.productCodes ?? FALLBACK_PRODUCT_CODES).slice(0, 3)),
        node: null,
        fallbackReason: 'LOW_CONFIDENCE',
      }),
    );
  }

  // ③ 매칭 실패 — 기본 3종을 낸다. `sortOrder` 앞에서 자르지 않는다.
  if (!route) {
    return wait(
      turn(sessionUuid, {
        mode: 'FALLBACK',
        message: copyText('NO_MATCH'),
        products: buildProducts(FALLBACK_PRODUCT_CODES),
        node: fallbackNode(),
        fallbackReason: 'UNKNOWN_SERVICE',
      }),
    );
  }

  // ④ 정상 추천 — 최대 3건
  return wait(turn(sessionUuid, { mode: 'NORMAL', message: recommendText(route.categoryCode), products: buildProducts(route.productCodes.slice(0, 3)) }));
}

export function sendChatAnswer(sessionUuid: string, _nodeId: string, optionId: string): Promise<ChatTurn> {
  const chosen = FALLBACK_TREE_OPTIONS.find(option => option.optionId === optionId);
  if (!chosen) {
    return wait(turn(sessionUuid, { mode: 'FALLBACK', message: copyText('FALLBACK_SYSTEM'), products: buildProducts(FALLBACK_PRODUCT_CODES), fallbackReason: 'UNKNOWN_SERVICE' }));
  }

  const product = CHAT_PRODUCT_SEED[chosen.productCodes[0]];
  return wait(turn(sessionUuid, { mode: 'NORMAL', message: recommendText(product?.categoryCode ?? ''), products: buildProducts(chosen.productCodes.slice(0, 3)) }));
}

/**
 * 서버는 `chat_event` 에 `CTA_CLICKED` 를 적재하고 깔때기(카드 노출 → 클릭 → 주문)를 센다.
 * 목에는 셀 곳이 없으니 받고 버린다 — 화면 흐름을 막지 않는 것이 여기서 할 일의 전부다.
 */
export function reportCtaClicked(sessionUuid: string, productCode: string): Promise<void> {
  void sessionUuid;
  void productCode;
  return wait(undefined);
}

// ── 어드민 — 문안 ────────────────────────────────────

/**
 * 있는 문안 + **비어 있는 자리**.
 *
 * 대조를 여기서 하는 것이 요점이다. 서버는 카탈로그 카테고리 전량과 되묻기 질문을 훑어
 * `TEMPLATE_RECOMMEND_*` · `FOLLOW_UP_*` 이 없는 것을 골라 준다. 목은 상품 시드가 가진
 * 카테고리로 같은 일을 한다 — 화면이 보는 모양은 같다.
 */
export function fetchChatCopies(): Promise<ChatCopyList> {
  const categories = new Map<string, string>();
  Object.values(CHAT_PRODUCT_SEED).forEach(product => categories.set(product.categoryCode, product.categoryLabel));

  const missingCategories: MissingChatCopy[] = Array.from(categories.entries())
    .filter(([code]) => !copies.some(row => row.copyKey === `TEMPLATE_RECOMMEND_${code}`))
    .map(([code, label]) => ({
      derivedFrom: 'CATEGORY',
      sourceKey: code,
      suggestedCopyKey: `TEMPLATE_RECOMMEND_${code}`,
      label: `추천 안내 — ${label}`,
      hint: `categoryCode = ${code}`,
    }));

  const missingFollowUps: MissingChatCopy[] = followUps
    .filter(row => !copies.some(copy => copy.copyKey === row.copyKey))
    .map(row => ({
      derivedFrom: 'FOLLOW_UP',
      sourceKey: row.followUpKey,
      suggestedCopyKey: `FOLLOW_UP_${row.followUpKey}`,
      label: `되묻기 — ${row.label}`,
      hint: `${row.followUpKey} 질문이 실제로 내는 문장`,
    }));

  return wait({ items: copies, missing: [...missingCategories, ...missingFollowUps] });
}

export function updateChatCopy(copyKey: string, textKo: string): Promise<ChatCopy> {
  const target = copies.find(row => row.copyKey === copyKey);
  if (!target) return Promise.reject(new ChatApiError(404, '해당 문안을 찾을 수 없습니다.'));

  const updated: ChatCopy = { ...target, textKo, updatedAt: nowStamp(), updatedBy: 'mock@lawkit.kr' };
  copies = copies.map(row => (row.copyKey === copyKey ? updated : row));

  return wait(updated);
}

export function createChatCopy(request: CreateChatCopyRequest): Promise<ChatCopy> {
  const copyKey = request.derivedFrom === 'CATEGORY' ? `TEMPLATE_RECOMMEND_${request.sourceKey}` : `FOLLOW_UP_${request.sourceKey}`;
  if (copies.some(row => row.copyKey === copyKey)) return Promise.reject(new ChatApiError(409, '이미 있는 문안입니다.'));

  const created: ChatCopy = {
    id: Math.max(0, ...copies.map(row => row.id)) + 1,
    copyKey,
    label: copyKey,
    hint: request.derivedFrom === 'CATEGORY' ? `categoryCode = ${request.sourceKey}` : `${request.sourceKey} 질문이 실제로 내는 문장`,
    textKo: request.textKo,
    category: request.derivedFrom === 'CATEGORY' ? 'TEMPLATE' : 'FOLLOW_UP',
    sortOrder: Math.max(0, ...copies.map(row => row.sortOrder)) + 10,
    updatedAt: nowStamp(),
    updatedBy: 'mock@lawkit.kr',
  };
  copies = [...copies, created];

  return wait(created);
}

// ── 어드민 — 되묻기 질문 ─────────────────────────────

export function fetchFollowUps(): Promise<ChatFollowUp[]> {
  return wait(followUps);
}

/** 질문과 그 문안을 **한 번에** 만든다. 문안이 없는 질문을 남기지 않는다. */
export function createFollowUp(request: CreateFollowUpRequest): Promise<ChatFollowUp> {
  if (followUps.some(row => row.followUpKey === request.followUpKey)) return Promise.reject(new ChatApiError(409, '이미 있는 질문 키입니다.'));

  const copyKey = `FOLLOW_UP_${request.followUpKey}`;
  const created: ChatFollowUp = {
    id: Math.max(0, ...followUps.map(row => row.id)) + 1,
    followUpKey: request.followUpKey,
    label: request.label,
    criterion: request.criterion,
    copyKey,
    categoryCode: request.categoryCode,
    enabled: request.enabled,
    sortOrder: Math.max(0, ...followUps.map(row => row.sortOrder)) + 10,
    updatedAt: nowStamp(),
    updatedBy: 'mock@lawkit.kr',
  };

  followUps = [...followUps, created];
  copies = [
    ...copies,
    {
      id: Math.max(0, ...copies.map(row => row.id)) + 1,
      copyKey,
      label: `되묻기 — ${request.label}`,
      hint: `${request.followUpKey} 질문이 실제로 내는 문장`,
      textKo: request.textKo,
      category: 'FOLLOW_UP',
      sortOrder: Math.max(0, ...copies.map(row => row.sortOrder)) + 10,
      updatedAt: nowStamp(),
      updatedBy: 'mock@lawkit.kr',
    },
  ];

  return wait(created);
}

export function updateFollowUp(id: number, request: UpdateFollowUpRequest): Promise<ChatFollowUp> {
  const target = followUps.find(row => row.id === id);
  if (!target) return Promise.reject(new ChatApiError(404, '해당 질문을 찾을 수 없습니다.'));

  const updated: ChatFollowUp = { ...target, ...request, updatedAt: nowStamp(), updatedBy: 'mock@lawkit.kr' };
  followUps = followUps.map(row => (row.id === id ? updated : row));

  return wait(updated);
}

// ── 어드민 — 차단 규칙 ───────────────────────────────

export function fetchGuardRules(): Promise<ChatGuardRule[]> {
  return wait(guardRules);
}

export function updateGuardRule(id: number, request: UpdateGuardRuleRequest): Promise<ChatGuardRule> {
  const target = guardRules.find(row => row.id === id);
  if (!target) return Promise.reject(new ChatApiError(404, '해당 규칙을 찾을 수 없습니다.'));

  const updated: ChatGuardRule = { ...target, ...request, updatedAt: nowStamp(), updatedBy: 'mock@lawkit.kr' };
  guardRules = guardRules.map(row => (row.id === id ? updated : row));

  return wait(updated);
}

// ── 어드민 — 100건 돌려보기 ──────────────────────────

/**
 * 기대와 실제를 맞춰 판정한다.
 *
 * **`OVER_BLOCK` 과 `UNDER_BLOCK` 을 `MISMATCH` 로 뭉개지 않는다.** 셋은 고칠 방향이 다르다 —
 * 과차단이면 규칙을 좁히고, 미차단이면 넓히고, 그 밖은 케이스를 다시 본다.
 */
function verdictOf(expected: DryRunOutcome, actual: DryRunOutcome): DryRunVerdict {
  if (expected === actual) return 'PASS';
  if (expected === 'PASS') return 'OVER_BLOCK';
  if (expected === 'REFUSAL' && actual === 'PASS') return 'UNDER_BLOCK';
  return 'MISMATCH';
}

/**
 * 서버가 하는 계산. 규칙을 끄면 `UNDER_BLOCK` 이, 넓히면 `OVER_BLOCK` 이 그 자리에서 뜬다 —
 * 그것이 이 화면의 존재 이유다(설계 §11.2).
 */
export function dryRunGuardRules(request: DryRunRequest): Promise<DryRunResult> {
  const usingDraft = Array.isArray(request.rules);
  const rules: DryRunRule[] = request.rules ?? guardRules;

  const cases: DryRunCase[] = DRY_RUN_CASE_SEED.map(seed => {
    const hit = rules.find(rule => rule.enabled && rule.stage === seed.stage && matches(rule.pattern, seed.input));
    const actual: DryRunOutcome = hit ? 'REFUSAL' : seed.baseline;

    return {
      id: seed.id,
      category: seed.category,
      stage: seed.stage,
      input: seed.input,
      expected: seed.expected,
      actual,
      verdict: verdictOf(seed.expected, actual),
      matchedRuleId: hit?.id ?? null,
      matchedPattern: hit?.pattern ?? null,
    };
  });

  const ruleHits: DryRunRuleHit[] = rules
    .filter((rule): rule is DryRunRule & { id: number } => rule.id !== null)
    .map(rule => {
      const hits = cases.filter(item => item.matchedRuleId === rule.id);
      return { ruleId: rule.id, pattern: rule.pattern, stage: rule.stage, hitCount: hits.length, overBlockCount: hits.filter(item => item.verdict === 'OVER_BLOCK').length };
    })
    .filter(hit => hit.hitCount > 0);

  return wait({
    ranAt: nowStamp(),
    totalCases: cases.length,
    ruleSetSource: usingDraft ? 'DRAFT' : 'SAVED',
    summary: {
      pass: cases.filter(item => item.verdict === 'PASS').length,
      overBlock: cases.filter(item => item.verdict === 'OVER_BLOCK').length,
      underBlock: cases.filter(item => item.verdict === 'UNDER_BLOCK').length,
      mismatch: cases.filter(item => item.verdict === 'MISMATCH').length,
    },
    cases,
    ruleHits,
  });
}
